// components/BookingAddonsSection.jsx (updated handleSaveProducts)
import { useState } from "react";
import { FiCheck, FiTrash2, FiMinus, FiPlus, FiX } from "react-icons/fi";
import { FaPlus } from "react-icons/fa";
import OtherChargesDrawer from "../common/OtherChargesDrawer";
import ProductCatalog from "../common/ProductCatalog";
import SelectAddOnsDrawer from "../../pages/Orders/SelectAddOnsDrawer";

const BookingAddonsSection = ({
  addons = [],
  products = [],
  otherCharges = [],
  onAddonsChange,
  onProductsChange,
  onOtherChargesChange,
  serviceCategoryId,
  petTypeId,
  productsEndpoint = "/vendorproduct/getAvailableInventoryProducts",
  showQuantityControls = true,
  serviceTypeName = "",
  isClosed = false,
  drawerWidth = "w-full",
}) => {
  const [addonsDrawerOpen, setAddonsDrawerOpen] = useState(false);
  const [productsDrawerOpen, setProductsDrawerOpen] = useState(false);
  const [otherChargesDrawerOpen, setOtherChargesDrawerOpen] = useState(false);

  // ── Merging handlers ──
  const handleSaveAddons = (newAddonItems) => {
    const normalized = Array.isArray(newAddonItems)
      ? newAddonItems
      : [newAddonItems];
    const merged = [...addons];
    normalized.forEach((newItem) => {
      const idx = merged.findIndex(
        (item) => item.addon_id === newItem.addon_id,
      );
      if (idx !== -1) {
        merged[idx] = {
          ...merged[idx],
          quantity: (merged[idx].quantity || 0) + (newItem.quantity || 1),
        };
      } else {
        merged.push(newItem);
      }
    });
    onAddonsChange(merged);
    setAddonsDrawerOpen(false);
  };

  // ✅ handleSaveProducts – matched exactly to BookingDetailsDrawer version
  const handleSaveProducts = (updatedProducts) => {
    setProductsDrawerOpen(false);

    const roundToTwo = (num) => {
      if (num === null || num === undefined || isNaN(num)) return 0;
      return Math.round((parseFloat(num) + Number.EPSILON) * 100) / 100;
    };

    const formattedProducts = updatedProducts.map((product) => {
      // Get values from product (using the correct field names from ProductCatalog)
      const effectivePrice = Number(
        product.sale_price_after_discount ||
          product.effective_sale_price ||
          product.price ||
          0,
      );
      const mrpPrice = Number(
        product.mrp || product.mrp_price || product.sale_price || 0,
      );
      const gstSlab = Number(product.gst_slab || product.tax_percentage || 0);
      const quantity = Number(product.quantity || 1);

      // ✅ CRITICAL: Calculate price without tax
      let priceWithoutTax = effectivePrice;
      let gstAmount = 0;

      if (gstSlab > 0 && effectivePrice > 0) {
        // priceWithoutTax = effectivePrice / (1 + gstSlab/100)
        priceWithoutTax = effectivePrice / (1 + gstSlab / 100);
        gstAmount = effectivePrice - priceWithoutTax;
      }

      return {
        // Preserve all original product data first
        ...product,

        // Core identifiers — these must come AFTER the spread to win
        product_id: product.id || product._id,
        product_name: product.name,

        // Quantity
        quantity: quantity,

        // ✅ CRITICAL: These fields MUST be correct
        unit_price: roundToTwo(effectivePrice),
        price: roundToTwo(priceWithoutTax),
        mrp_price: roundToTwo(mrpPrice),
        sale_price: roundToTwo(mrpPrice),
        total_price: roundToTwo(effectivePrice * quantity),

        // GST fields
        gst: roundToTwo(gstAmount),
        gstAmount: roundToTwo(gstAmount),
        gstAmountAfterDiscount: roundToTwo(gstAmount),
        tax_percentage: gstSlab,
        tax_amount: roundToTwo(gstAmount),

        // Amount breakdowns
        amount_without_tax: roundToTwo(priceWithoutTax),
        amount_with_tax: roundToTwo(effectivePrice),
        amount_before_discount: roundToTwo(mrpPrice),
        amount_after_discount: roundToTwo(effectivePrice),

        // Additional fields
        description: product.description || "",
        sku_number: product.sku_number || "",
        gst_slab: gstSlab,
      };
    });

    // Merge with existing products
    const merged = [...products];
    formattedProducts.forEach((newItem) => {
      const existingIndex = merged.findIndex(
        (item) => item.product_id === newItem.product_id,
      );
      if (existingIndex !== -1) {
        const existing = merged[existingIndex];
        const newQuantity = existing.quantity + newItem.quantity;
        merged[existingIndex] = {
          ...newItem,
          quantity: newQuantity,
          total_price: newItem.unit_price * newQuantity,
          amount_without_tax: newItem.amount_without_tax * newQuantity,
        };
      } else {
        merged.push(newItem);
      }
    });
    onProductsChange(merged);
  };

  const handleSaveOtherCharges = (charges) => {
    onOtherChargesChange(charges);
    setOtherChargesDrawerOpen(false);
  };

  // ── Delete & quantity helpers ──
  const removeAddon = (i) => {
    const updated = addons.filter((_, idx) => idx !== i);
    onAddonsChange(updated);
  };
  const removeProduct = (i) => {
    const updated = products.filter((_, idx) => idx !== i);
    onProductsChange(updated);
  };
  const removeCharge = (i) => {
    const updated = otherCharges.filter((_, idx) => idx !== i);
    onOtherChargesChange(updated);
  };

  const adjustAddonQty = (i, delta) => {
    const updated = [...addons];
    const newQty = (updated[i].quantity || 1) + delta;
    if (newQty < 1) return removeAddon(i);
    updated[i] = { ...updated[i], quantity: newQty };
    onAddonsChange(updated);
  };

  const adjustProductQty = (i, delta) => {
    const updated = [...products];
    const newQty = (updated[i].quantity || 1) + delta;
    if (newQty < 1) return removeProduct(i);
    updated[i] = { ...updated[i], quantity: newQty };
    onProductsChange(updated);
  };

  // ── Render helpers (exactly matching PetServiceList style) ──
  const renderAddonList = () => {
    if (!addons || addons.length === 0) return null;
    return (
      <div className="mt-3">
        <h4 className="text-sm font-medium text-gray-700 mb-2">
          Add-on Services
        </h4>
        <div className="space-y-2">
          {addons.map((addon, idx) => (
            <div
              key={idx}
              className="flex items-center justify-between p-2 bg-green-50 rounded-lg border border-green-100"
            >
              <div className="flex items-center gap-2">
                <FiCheck className="w-3 h-3 text-green-600" />
                <span className="text-xs font-medium text-gray-800">
                  {addon.addon_name || addon.name || `Addon ${idx + 1}`}
                  {/* {addon?.quantity &&
                    addon.quantity > 1 &&
                    ` x ${addon.quantity}`} */}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-green-700">
                  ₹{Number(addon.addon_price || addon.price || 0).toFixed(2)}
                </span>
                {/* {addon?.quantity && addon.quantity > 1 && (
                  <button
                    onClick={() => adjustAddonQty(idx, -1)}
                    className="text-blue-500 hover:text-blue-700"
                    title="Decrease quantity"
                  >
                    <FiMinus className="w-3 h-3" />
                  </button>
                )} */}
                {!isClosed && (
                  <button
                    onClick={() => removeAddon(idx)}
                    className="text-red-500 hover:text-red-700"
                    title="Remove addon"
                  >
                    <FiTrash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderProductList = () => {
    if (!products || products.length === 0) return null;
    return (
      <div className="mt-3">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Products</h4>
        <div className="space-y-2">
          {products.map((product, idx) => {
            const mrpPrice = Number(
              product.mrp_price || product.sale_price || 0,
            );
            const effectivePrice = Number(product.unit_price || 0);
            const priceWithoutTax = Number(
              product.price || product.amount_without_tax || 0,
            );
            const gstAmount = Number(product.gst || product.gstAmount || 0);
            const quantity = Number(product.quantity || 1);
            const totalEffective = effectivePrice * quantity;
            const totalWithoutTax = priceWithoutTax * quantity;
            const totalGst = gstAmount * quantity;

            return (
              <div
                key={idx}
                className="relative flex items-center justify-between p-2 pr-7 bg-blue-50 rounded-lg border border-blue-100"
              >
                {!isClosed && (
                  <button
                    onClick={() => removeProduct(idx)}
                    className="absolute top-1 right-1 text-red-500 hover:text-red-700"
                  >
                    <FiTrash2 className="w-4 h-4" />
                  </button>
                )}

                <div className="flex items-start gap-2 flex-col">
                  <span className="text-sm font-medium text-gray-800 flex items-center gap-1">
                    <span>{product.product_name || product.name}</span>
                    {product.gst_slab > 0 && (
                      <span className="italic text-xs">
                        (gst {product.gst_slab}%)
                      </span>
                    )}
                  </span>
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    
                    {!isClosed && (
                      <>
                        {quantity > 1 && (
                          <button
                            onClick={() => adjustProductQty(idx, -1)}
                            className="text-black-500 hover:text-black-700"
                          >
                            <FiMinus className="w-4 h-4" />
                          </button>
                        )}
                        {quantity === 1 && (
                          <button
                            onClick={() => removeProduct(idx)}
                            className="text-black-500 hover:text-black-700"
                          >
                            <FiTrash2 className="w-3 h-3" />
                          </button>
                        )}
                      </>
                    )}
                    <span>{quantity}</span>
                    {!isClosed && (
                      <button
                        onClick={() => adjustProductQty(idx, 1)}
                        className="text-black-500 hover:text-black-700"
                      >
                        <FiPlus className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="flex flex-col items-end">
                      <span className="text-gray-500 line-through text-sm">
                        ₹{mrpPrice.toFixed(2)}
                      </span>
                      <span className="text-green-600 font-bold">
                        ₹{effectivePrice.toFixed(2)}
                      </span>
                    </div>
                    <div className="text-green-600 font-bold min-w-[70px] text-right">
                      ₹{totalEffective.toFixed(2)}
                    </div>
                    <div className="flex flex-col items-end">
                      <span className="text-green-600 font-bold">
                        ₹{totalWithoutTax.toFixed(2)}
                      </span>
                      <span className="text-gray-600 italic text-xs">
                        +₹{totalGst.toFixed(2)} taxes
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderOtherChargesList = () => {
    if (!otherCharges || otherCharges.length === 0) return null;
    return (
      <div className="mt-3">
        <h4 className="text-sm font-medium text-gray-700 mb-2">
          Other Charges
        </h4>
        <div className="space-y-2">
          {otherCharges.map((charge, idx) => (
            <div
              key={idx}
              className="flex items-center justify-between p-2 bg-purple-50 rounded-lg border border-purple-100"
            >
              <div>
                <span className="text-xs font-medium text-gray-800">
                  {charge.description || charge.name || "Charge"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-purple-700">
                  ₹{Number(charge.amount || 0).toFixed(2)}
                </span>
                {!isClosed && (
                  <button
                    onClick={() => removeCharge(idx)}
                    className="text-red-500 hover:text-red-700"
                    title="Remove charge"
                  >
                    <FiTrash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="mt-3">
      {!isClosed && (
        <div className="flex justify-end gap-4 mt-3 pt-3 border-t border-gray-200">
          <button
            onClick={() => setAddonsDrawerOpen(true)}
            className="text-teal-600 hover:text-teal-700 font-semibold text-sm flex items-center gap-1"
          >
            <FaPlus size={12} /> Add Add‑ons
          </button>
          <button
            onClick={() => setProductsDrawerOpen(true)}
            className="text-teal-600 hover:text-teal-700 font-semibold text-sm flex items-center gap-1"
          >
            <FaPlus size={12} /> Add Products
          </button>
          <button
            onClick={() => setOtherChargesDrawerOpen(true)}
            className="text-teal-600 hover:text-teal-700 font-semibold text-sm flex items-center gap-1"
          >
            <FaPlus size={12} /> Other Charges
          </button>
        </div>
      )}

      {renderAddonList()}
      {renderProductList()}
      {renderOtherChargesList()}

      {addonsDrawerOpen && (
        <SelectAddOnsDrawer
          isOpen={addonsDrawerOpen}
          onClose={() => setAddonsDrawerOpen(false)}
          onSave={handleSaveAddons}
          serviceCategoryId={serviceCategoryId}
          petTypeId={petTypeId}
          selectedAddons={addons}
          serviceTypeName={serviceTypeName}
          isFullWidth={drawerWidth === "w-full"}
          drawerWidth={drawerWidth}
        />
      )}
      {productsDrawerOpen && (
        <ProductCatalog
          isOpen={productsDrawerOpen}
          onClose={() => setProductsDrawerOpen(false)}
          onCartUpdate={handleSaveProducts}
          apiEndpoint={productsEndpoint}
          isFullWidth={drawerWidth === "w-full"}
          drawerWidth={drawerWidth}
        />
      )}
      {otherChargesDrawerOpen && (
        <OtherChargesDrawer
          isOpen={otherChargesDrawerOpen}
          onClose={() => setOtherChargesDrawerOpen(false)}
          onSave={handleSaveOtherCharges}
          initialCharges={otherCharges}
          isFullWidth={drawerWidth === "w-full"}
          drawerWidth={drawerWidth}
        />
      )}
    </div>
  );
};

export default BookingAddonsSection;
